import { LoginArea } from '@/components/auth/LoginArea';
import { ScaryModeToggle } from '@/components/ScaryModeToggle';
import { Skull, Bat, Zap } from 'lucide-react';
import { useTheme } from '@/hooks/useTheme';

export function CemeteryHeader() {
  const { theme } = useTheme();
  const isScary = theme === 'dark';
  const isNightmare = theme === 'nightmare';

  return (
    <header className={`relative border-b border-primary/30 bg-card/60 backdrop-blur-md ${isNightmare ? 'nightmare-mode' : ''}`}>
      {/* Fog overlay */}
      {(isScary || isNightmare) && <div className="fog-overlay" />}

      {/* Nightmare mode effects */}
      {isNightmare && (
        <>
          <div className="nightmare-flash" />
          <div className="absolute top-0 left-10 w-1 h-20 blood-drip" style={{ animationDelay: '0s' }} />
          <div className="absolute top-0 right-20 w-1 h-20 blood-drip" style={{ animationDelay: '1s' }} />
          <div className="absolute top-0 left-1/3 w-1 h-20 blood-drip" style={{ animationDelay: '2s' }} />
        </>
      )}

      <div className="container mx-auto px-4 py-6 relative z-10">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          {/* Logo and Title */}
          <div className="flex items-center gap-4">
            <div className="relative">
              <Skull className={`h-12 w-12 text-primary ${
                isScary ? 'animate-pulse' :
                isNightmare ? 'skull-float' : ''
              }`} />
              {(isScary || isNightmare) && (
                <div className={`absolute inset-0 blur-xl -z-10 ${
                  isNightmare ? 'bg-destructive/50' : 'bg-primary/30'
                }`} />
              )}
            </div>
            <div>
              <h1 className={`text-4xl md:text-5xl font-creepster text-primary leading-none ${
                isNightmare ? 'animate-pulse' : ''
              }`}>
                Block Cemetery
              </h1>
              <p className={`text-sm mt-1 font-gothic ${
                isNightmare ? 'text-destructive' : 'text-muted-foreground'
              }`}>
                Where Bitcoin Blocks Go to Rest... Until the Next One Rises
              </p>
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-4">
            <ScaryModeToggle />
            <LoginArea className="max-w-48" />
          </div>
        </div>
      </div>

      {/* Decorative elements */}
      {(isScary || isNightmare) && (
        <>
          {/* Regular bats for scary mode */}
          {isScary && (
            <>
              <div className="absolute top-4 left-8 text-2xl opacity-30 bat-fly">🦇</div>
              <div className="absolute top-8 right-16 text-2xl opacity-30 bat-fly" style={{ animationDelay: '2s' }}>🦇</div>
            </>
          )}

          {/* Vampire bats and skulls for nightmare mode */}
          {isNightmare && (
            <>
              <div className="absolute top-4 left-8 text-3xl opacity-40 vampire-bat">🦇</div>
              <div className="absolute top-8 right-16 text-3xl opacity-40 vampire-bat" style={{ animationDelay: '1s' }}>🦇</div>
              <div className="absolute top-12 left-1/4 text-4xl opacity-30 skull-float">💀</div>
              <div className="absolute top-6 right-1/3 text-4xl opacity-30 skull-float" style={{ animationDelay: '1.5s' }}>💀</div>
              <div className="absolute top-20 left-1/2 text-4xl opacity-30 skull-float" style={{ animationDelay: '2.5s' }}>💀</div>
              <div className="absolute top-2 right-10 text-3xl opacity-30 vampire-bat" style={{ animationDelay: '3s' }}>🧛</div>
              <div className="absolute top-16 left-20 text-3xl opacity-30 vampire-bat" style={{ animationDelay: '4s' }}>🧛</div>
            </>
          )}
        </>
      )}
    </header>
  );
}
